/**
 * apiClient — the single entry point for backend calls.
 *
 * Replaces three patterns that were repeated across the app:
 *   1. The base URL, re-derived in ~23 files.
 *   2. Raw `fetch()` with no request deduplication, so two components mounting
 *      at once issued the same request twice.
 *   3. No caching, so navigating back to a page refetched data that had not
 *      changed (place lists, clusters, and district data are effectively static
 *      between deploys).
 *
 * Caching is sessionStorage-backed rather than in-memory so it also survives a
 * reload or a restored tab — the case a module-level Map silently misses.
 */

import { useCallback, useEffect, useRef } from "react";

// ── Base URL ────────────────────────────────────────────────────────────────
// Normalised to exactly one trailing slash so callers can pass "api/data/places"
// without worrying about double slashes.
const RAW_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_LOCAL_BACKEND_URL ||
  "http://localhost:5000/";

export const API_BASE_URL = RAW_BASE.endsWith("/") ? RAW_BASE : `${RAW_BASE}/`;

/** Build an absolute backend URL. `params` skips null/undefined/"" values. */
export function apiUrl(path, params) {
  const url = new URL(path.replace(/^\//, ""), API_BASE_URL);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, v);
    }
  }
  return url.toString();
}

// ── Cache ───────────────────────────────────────────────────────────────────
const CACHE_PREFIX = "journim:api:";
// sessionStorage is ~5MB. A single state's place list can be sizeable, so skip
// persisting anything unusually large rather than evicting everything else.
const MAX_ENTRY_BYTES = 250 * 1024;

export const TTL = {
  /** Place/cluster/district data — only changes on a data re-import. */
  STATIC: 30 * 60 * 1000,
  /** Per-user data that a mutation elsewhere may invalidate. */
  SHORT: 60 * 1000,
};

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return undefined;
    const entry = JSON.parse(raw);
    if (Date.now() >= entry.expiry) {
      sessionStorage.removeItem(CACHE_PREFIX + key);
      return undefined;
    }
    return entry.value;
  } catch {
    // Malformed entry, or storage unavailable (Safari private mode).
    return undefined;
  }
}

function writeCache(key, value, ttlMs) {
  if (!ttlMs) return;
  try {
    const payload = JSON.stringify({ value, expiry: Date.now() + ttlMs });
    if (payload.length > MAX_ENTRY_BYTES) return;
    try {
      sessionStorage.setItem(CACHE_PREFIX + key, payload);
    } catch {
      // Almost always QuotaExceededError. Drop our own oldest entries and retry
      // once; never touch keys we don't own.
      evictOldest(10);
      sessionStorage.setItem(CACHE_PREFIX + key, payload);
    }
  } catch {
    // Caching is best-effort — a storage failure must never break the request.
  }
}

function evictOldest(count) {
  try {
    const entries = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (!k || !k.startsWith(CACHE_PREFIX)) continue;
      try {
        entries.push([k, JSON.parse(sessionStorage.getItem(k)).expiry || 0]);
      } catch {
        entries.push([k, 0]); // unparseable — evict first
      }
    }
    entries.sort((a, b) => a[1] - b[1]);
    entries.slice(0, count).forEach(([k]) => sessionStorage.removeItem(k));
  } catch {
    /* best-effort */
  }
}

/** Drop cached entries whose key contains `substring` (e.g. "api/wishlist"). */
export function invalidate(substring) {
  try {
    const doomed = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(CACHE_PREFIX) && k.includes(substring)) doomed.push(k);
    }
    doomed.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* best-effort */
  }
}

export function clearApiCache() {
  invalidate("");
}

// ── In-flight deduplication ─────────────────────────────────────────────────
const inFlight = new Map(); // cacheKey -> Promise

/**
 * Reject for *this* caller when its signal aborts, without cancelling the shared
 * request other callers are still waiting on. Aborting a deduplicated fetch
 * outright would break every peer that joined it.
 */
function withSignal(promise, signal) {
  if (!signal) return promise;
  if (signal.aborted) {
    return Promise.reject(new DOMException("Aborted", "AbortError"));
  }
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(new DOMException("Aborted", "AbortError"));
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(resolve, reject).finally(() => {
      signal.removeEventListener("abort", onAbort);
    });
  });
}

async function runFetch(url, init, timeoutMs) {
  // The shared request owns its own controller so it is independent of any one
  // caller's signal; timeoutMs guards against a hung backend.
  const controller = new AbortController();
  const timer = timeoutMs ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const res = await fetch(url, { ...init, signal: controller.signal });
    if (!res.ok) {
      const err = new Error(`Request failed: ${res.status} ${res.statusText}`);
      err.status = res.status;
      err.url = url;
      throw err;
    }
    return res.status === 204 ? null : await res.json();
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * GET with deduplication and optional caching.
 *
 * @param {string} path            e.g. "api/data/places"
 * @param {object} [opts]
 * @param {object} [opts.params]   query params (empty values dropped)
 * @param {number} [opts.ttl]      cache lifetime in ms; omit to skip caching
 * @param {AbortSignal} [opts.signal]
 * @param {number} [opts.timeoutMs=15000]
 * @param {boolean} [opts.force]   bypass a cache hit, still dedupes + refills
 */
export function apiGet(path, opts = {}) {
  const {
    params,
    ttl,
    signal,
    timeoutMs = 15000,
    force = false,
    headers,
    credentials,
  } = opts;
  const url = apiUrl(path, params);
  // Auth headers vary per user, so they are part of the identity of the request:
  // without this, two users in one tab could share a cached/deduped response.
  const authTag = headers?.["X-Clerk-User-Id"] || headers?.Authorization || "";
  const key = `GET ${url}${authTag ? ` @${authTag}` : ""}`;

  if (!force) {
    const cached = readCache(key);
    if (cached !== undefined) return withSignal(Promise.resolve(cached), signal);
  }

  let pending = inFlight.get(key);
  if (!pending) {
    pending = runFetch(url, { method: "GET", headers, credentials }, timeoutMs)
      .then((data) => {
        writeCache(key, data, ttl);
        return data;
      })
      .finally(() => inFlight.delete(key));
    inFlight.set(key, pending);
  }

  return withSignal(pending, signal);
}

/**
 * POST. Not cached and not deduplicated by default — POSTs are usually
 * mutations. Pass `dedupe: true` for read-shaped POSTs (the itinerary endpoints
 * take their query in the body) to collapse duplicate concurrent submissions.
 */
export function apiPost(path, body, opts = {}) {
  const {
    signal,
    timeoutMs = 30000,
    dedupe = false,
    headers,
    credentials,
    method = "POST",
  } = opts;
  const url = apiUrl(path);
  const init = {
    method,
    headers: { "Content-Type": "application/json", ...headers },
    body: JSON.stringify(body ?? {}),
    credentials,
  };

  if (!dedupe) return runFetch(url, init, timeoutMs);

  const key = `POST ${url} ${init.body}`;
  let pending = inFlight.get(key);
  if (!pending) {
    pending = runFetch(url, init, timeoutMs).finally(() => inFlight.delete(key));
    inFlight.set(key, pending);
  }
  return withSignal(pending, signal);
}

// ── React helper ────────────────────────────────────────────────────────────
/**
 * Returns `getSignal()`, which yields a fresh AbortSignal and cancels the
 * previous one (latest-wins), plus `abort()`. Everything is aborted on unmount,
 * so a page that navigates away stops applying results to a dead component.
 *
 * Because aborting only detaches this caller (see `withSignal`), an in-flight
 * shared request keeps running for anyone else who joined it.
 */
export function useAbortableRequest() {
  const ref = useRef(null);

  useEffect(() => () => ref.current?.abort(), []);

  const getSignal = useCallback(() => {
    ref.current?.abort();
    ref.current = new AbortController();
    return ref.current.signal;
  }, []);

  const abort = useCallback(() => ref.current?.abort(), []);

  return { getSignal, abort };
}

/** True for errors raised by an aborted caller — use to skip setState. */
export function isAbortError(err) {
  return err?.name === "AbortError";
}
